/*!
 * @Author: leo
 * @Date: 2019-12-25 20:55:00
 * @Last Modified by: leo
 * @Last Modified time: 2019-12-25 20:55:00
 * @Instruduction: 用户服务接口
 */

import xhr from './xhr/index'
import { IExpData } from '@/types/reporter-types'

interface IReporterListParams {
    media_id: any
    reporter_name: string
    page_index: number   // 第几页
    page_size: number    // 每页条数
}

interface IUpdateReporterParams {
    id: number 
    type?: number
    name?: string 
    avatar?: string
    intro?: string
    media?: {
        id: number
        name: string
    }
}

interface IAddReporterParams {
    name: string, 
    gender?: number
    avatar?: string,
    intro?: string,
    media: {
        id: number,
        name: string
    },
    work_exps?: IExpData[]
}

interface INewsListParams {
    reporter_id: number   // 记者 ID
    page_index: number    // 第几页
    page_size: number     // 每页条数
}

interface IGetReporterParams {
    id: number      // 记者 ID
}

interface IGetExpsParams {
    id: number      // 记者 ID
}

interface IDeleteExpParams {
    id: number      // 记者 ID
}

interface IDeleteReporterParams {
    id: number      // 记者 ID
}

interface IUpdateExpParams {
    id: number
    media: {
        id: number
        name: string
    }
    position: string
    content: string
    start_at: string
    end_at: string
}

interface IAddExpParams {
    rid: number
    media: {
        id: number
        name: string
    }
    position: string
    content: string
    start_at: string
    end_at: string
}

interface IRepeatListParams {
    page_index: number    // 第几页
    page_size: number     // 每页条数
}

interface IReporterListByNameParams {
    name: string
    page_index: number    // 第几页
    page_size: number     // 每页条数
}

interface IMergeParams {
    reporter_ids: string[]
}

interface IGetMergeLogsParams {
    reporter_id?: number
    page_index?: number
    page_size?: number
}

interface IGetToDeletedListParams {
    status?: number
    page_index?: number
    page_size?: number
}

interface IUpdateToDeletedStatusParams {
    id: number
    operate: number
}

interface IGetReporterCollectionListParams {
    page_index?: number    // 第几页
    page_size?: number     // 每页显示数量
}

interface IGetReporterCollectionDetailParams {
    id: number             // 记者集合 ID
}

interface IAddReporterCollectionParams {
    name: string,                       // 集合名字 (2~10 字)
    cover_img?: string,                 // 配图
    order?: number,                     // 顺序
    memo?: string                       // 简介 (100 字以内)
}

interface IUpdateReporterCollectionParams {
    id: number,                           // 集合 id
    name?: string,                        // 集合名字 (2~10 字)
    cover_img?: string,                   // 配图
    order?: number,                       // 顺序
    memo?: string                         // 简介 (100 字以内)
}

interface IDeleteReporterCollectionParams {
    id: number                            // 集合 id
}

interface IGetReportersFromCollectionParams {
    id: number,                           // 集合id (传0：返回推荐记者集合)
    page_index?: number,                  // 第几页
    page_size?: number                    // 每页条数
}

interface IAddReporterToCollectionParams {
    set_id: number,                       // 集合id(0：推荐记者集合id)
    reporter_id: number,                  // 记者id
    order: number,                        // 顺序
    note?: string                         // 简介(100字以内)
}

interface IUpdateReporterAtCollectionParams {
    set_id: number,                       // 集合id(0：推荐记者集合id)
    reporter_id: number,                  // 记者id
    order: number,                        // 顺序
    note?: string                         // 简介(100字以内)
}

interface IDeleteReporterAtCollectionParams {
    set_id: number,                       // 集合id(0：推荐记者集合id)
    reporter_id: number                   // 记者id
}

interface IGetReporterRankParams {
    cycle?: number,                      // 榜单周期：1:日(默认) 2:周 3:月
    news_type?: number,                  // 领域id
    media_id?: number,                   // 媒体id
    rank_date?: string,                  // 榜单日期(下面date_ranges中的start)
    page_index?: number,                 // 第几页(默认1)
    page_size?: number                   // 每页条数(默认20)
}

interface IGetAddNewsListParams {
    status?: number,                     // 状态 0:全部(默认) 1:未处理 2:已处理 3:不用管
    sort?: string,                       // 排序字段 created_at(默认) updated_at
    page_size?: number,                  // 每页条数
    page_index?: number                  // 第几页
}

interface IUpdateAddNewsStatusParams {
    id: number,                          // 编号
    status: number                       // 状态 1:未处理 2:已处理 3:不用管
}

class ReporterService {
    /** 获取记者列表 */
    public async getList(params: IReporterListParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporters',
            params: params 
        })
        return result
    }
    /** 获取重复记者列表 */
    public async getGroups(params: IRepeatListParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporter/groups',
            params: params 
        })
        return result
    }
    /** 根据记者名获取记者列表 */
    public async getReportersByName(params: IReporterListByNameParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporter/list',
            params: params 
        })
        return result
    }
    /** 更新记者 */
    public async update(params: IUpdateReporterParams) {
        const result = await xhr({
            method: 'put',
            url: '/reporter',
            params: params 
        })
        return result
    }
    /** 创建记者 */
    public async create(params: IAddReporterParams) {
        const result = await xhr({
            method: 'post',
            url: '/reporter',
            params: params 
        })
        return result
    }
    /** 删除记者 */
    public async delete(params: IDeleteReporterParams) {
        const result = await xhr({
            method: 'delete',
            url: '/reporter',
            params: params 
        })
        return result
    }
    /** 获取记者详情 */
    public async getDetail(params: IGetReporterParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporter',
            params: params 
        })
        return result
    }
    /** 获取记者的新闻列表 */
    public async getNewsList(params: INewsListParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporter/news_list',
            params: params 
        })
        return result
    }
    /** 搜索记者列表 */
    public async search(params: any) {
        const result = await xhr({
            method: 'get',
            url: '/reporter/available_reporters',
            params: params 
        })
        return result
    }
    /** 获取工作经历 */
    public async getExps(params: IGetExpsParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporter/work_exp/list',
            params: params 
        })
        return result
    }
    /** 删除工作经历 */
    public async deleteExp(params: IDeleteExpParams) {
        const result = await xhr({
            method: 'delete',
            url: '/reporter/work_exp',
            params: params 
        })
        return result
    }
    /** 修改工作经历 */
    public async updateExp(params: IUpdateExpParams) {
        const result = await xhr({
            method: 'put',
            url: '/reporter/work_exp',
            params: params 
        })
        return result
    }
    /** 新增工作经历 */
    public async addExp(params: IAddExpParams) {
        const result = await xhr({
            method: 'post',
            url: '/reporter/work_exp',
            params: params 
        })
        return result
    }
    /** 合并记者 */
    public async merge(params: IMergeParams) {
        const result = await xhr({
            method: 'post',
            url: '/reporter/merge',
            params: params 
        })
        return result
    }
    /** 记者合并记录 */
    public async getMergeLogs(params: IGetMergeLogsParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporter/merge_logs',
            params: params 
        })
        return result
    }
    /** 获取待删除的记者 */
    public async getToDeletedList(params: IGetToDeletedListParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporter/to_be_del_list',
            params: params 
        })
        return result
    }
    /** 更新待删除记者状态 */
    public async updateToDeletedStatus(params: IUpdateToDeletedStatusParams) {
        const result = await xhr({
            method: 'put',
            url: '/reporter/to_be_del',
            params: params 
        })
        return result
    }

    /** 获取记者集合列表 */
    public async getReportCollectionList(params: IGetReporterCollectionListParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporter_set/list',
            params: params
        })
        return result
    }

    /** 获取集合详情 */
    public async getReporterCollectionDetail(params: IGetReporterCollectionDetailParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporter_set',
            params: params
        })
        return result
    }

    /** 新增记者集合 */ 
    public async addReporterCollection(params: IAddReporterCollectionParams) {
        const result = await xhr({
            method: 'post',
            url: '/reporter_set',
            params: params
        })
        return result
    }

    /** 修改记者集合 */ 
    public async updateReporterCollection(params: IUpdateReporterCollectionParams) {
        const result = await xhr({
            method: 'put',
            url: '/reporter_set',
            params: params
        })
        return result
    }

    /** 删除记者集合 */ 
    public async deleteReporterCollection(params: IDeleteReporterCollectionParams) {
        const result = await xhr({
            method: 'delete',
            url: '/reporter_set',
            params: params
        })
        return result
    }

    /** 获取集合下的记者，推荐记者 id: 0 */ 
    public async getReportersFromCollection(params: IGetReportersFromCollectionParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporter_set/reporters',
            params: params
        })
        return result

    }

    /** 集合中添加记者 */
    public async addReporterToCollection(params: IAddReporterToCollectionParams) {
        const result = await xhr({
            method: 'post',
            url: '/reporter_set/reporter',
            params: params
        })
        return result
    }

    /** 集合中修改记者 */
    public async updateReporterAtCollection(params: IUpdateReporterAtCollectionParams) {
        const result = await xhr({
            method: 'put',
            url: '/reporter_set/reporter',
            params: params
        })
        return result
    }

    /** 集合中删除记者 */
    public async deleteReporterAtCollection(params: IDeleteReporterAtCollectionParams) {
        const result = await xhr({
            method: 'delete',
            url: '/reporter_set/reporter',
            params: params
        })
        return result
    }

    /** 记者榜单 */
    public async getReporterRank(params: IGetReporterRankParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporter/ranks',
            params: params
        })
        return result
    }

    /** 新闻补录列表 */
    public async getAddNewsList(params: IGetAddNewsListParams) {
        const result = await xhr({
            method: 'get',
            url: '/reporter/add_news/list',
            params: params
        })
        return result
    }

    /** 新闻补录列表 */
    public async updateAddNewsStatus(params: IUpdateAddNewsStatusParams) {
        const result = await xhr({
            method: 'put',
            url: '/reporter/add_news',
            params: params
        })
        return result
    }
}

// 实例化后导出，全局单例
export default new ReporterService()
